import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Book } from '../types';

export interface Favorite {
  id?: string;
  userId: string;
  bookId: string;
  bookTitle: string;
  bookAuthor: string;
  bookCoverImage: string;
  bookPrice: number;
  createdAt: Date;
}

export const useFavorites = () => {
  const { currentUser } = useAuth();
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if a book is in user's favorites
  const isBookFavorited = (bookId: string): boolean => {
    return favorites.some(fav => fav.bookId === bookId);
  };

  // Add book to favorites
  const addToFavorites = async (book: Book): Promise<void> => {
    if (!currentUser) {
      throw new Error('You must be logged in to add favorites');
    }

    try {
      setLoading(true);
      setError(null);

      const newFavorite: Omit<Favorite, 'id'> = {
        userId: currentUser.uid,
        bookId: book.id,
        bookTitle: book.title,
        bookAuthor: book.author,
        bookCoverImage: book.coverImage,
        bookPrice: book.amount,
        createdAt: new Date()
      };

      await addDoc(collection(db, 'favorites'), newFavorite);
      
      // Note: The onSnapshot listener will automatically update the favorites state
    } catch (err) {
      console.error('Error adding to favorites:', err);
      setError('Failed to add to favorites');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Remove book from favorites
  const removeFromFavorites = async (bookId: string): Promise<void> => {
    if (!currentUser) {
      throw new Error('You must be logged in to remove favorites');
    }

    try {
      setLoading(true);
      setError(null);

      // Find the favorite document to delete
      const favoritesQuery = query(
        collection(db, 'favorites'),
        where('userId', '==', currentUser.uid),
        where('bookId', '==', bookId)
      );
      
      const favoritesSnapshot = await getDocs(favoritesQuery);
      
      if (!favoritesSnapshot.empty) {
        const favoriteDoc = favoritesSnapshot.docs[0];
        await deleteDoc(doc(db, 'favorites', favoriteDoc.id));
        // Note: The onSnapshot listener will automatically update the favorites state
      }
    } catch (err) {
      console.error('Error removing from favorites:', err);
      setError('Failed to remove from favorites');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Toggle favorite status
  const toggleFavorite = async (book: Book): Promise<void> => {
    if (isBookFavorited(book.id)) {
      await removeFromFavorites(book.id);
    } else {
      await addToFavorites(book);
    }
  };

  // Load user's favorites
  useEffect(() => {
    if (!currentUser) {
      setFavorites([]);
      return;
    }

    setLoading(true);

    // Set up real-time listener for favorites
    const favoritesQuery = query(
      collection(db, 'favorites'),
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(
      favoritesQuery,
      (snapshot) => {
        const favoritesData: Favorite[] = [];
        
        snapshot.forEach((doc) => {
          const favoriteData = doc.data();
          favoritesData.push({
            id: doc.id,
            userId: favoriteData.userId,
            bookId: favoriteData.bookId,
            bookTitle: favoriteData.bookTitle,
            bookAuthor: favoriteData.bookAuthor,
            bookCoverImage: favoriteData.bookCoverImage,
            bookPrice: favoriteData.bookPrice,
            createdAt: favoriteData.createdAt?.toDate() || new Date()
          });
        });

        setFavorites(favoritesData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error listening to favorites:', err);
        setError('Failed to load favorites');
        setLoading(false);
      }
    );

    // Cleanup subscription
    return () => unsubscribe();
  }, [currentUser]);

  return {
    favorites,
    loading,
    error,
    isBookFavorited,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite
  };
};
